/**
 * BarcodeScanner — live camera scanning for food packaging.
 *
 * Iter 52: dual-purpose now.
 *   1. Continuous barcode detection (EAN/UPC/QR) — passive, silent.
 *      Parent gets first shot at deciding what to do with a detected
 *      digit string (typically: try OFF, and on miss, transition to
 *      label OCR without showing the failure).
 *   2. Shutter button for LABEL CAPTURE — user taps, we grab the
 *      current video frame as a base64 JPEG and hand it to the parent
 *      for OCR. Barcode detection continues while the user aims.
 *
 * Reuses the html5-qrcode dependency Arena's QRScanner uses. Do NOT
 * modify Arena's implementation — this is a separate instance with
 * its own tuned config.
 *
 * Callbacks:
 *   onDetected(digits)      — barcode auto-detected (8-14 digit string)
 *   onCapture(imageBase64)  — shutter tapped, JPEG data URL
 *   onManualClick()         — user tapped "Enter code manually"
 *   labelMode (bool)        — parent-controlled hint: swap helper copy
 *                             to "point at the nutrition label" instead
 *                             of "point at the barcode"
 */
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Keyboard, AlertTriangle, Camera, Flashlight } from "lucide-react";

const CONTAINER_ID = "barcode-scanner-container";
const HINT_AFTER_MS = 15000;

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.QR_CODE,
];

function haptic() {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(40);
    }
  } catch { /* non-fatal */ }
}

export default function BarcodeScanner({
  onDetected,
  onCapture,
  onManualClick,
  active = true,
  labelMode = false,
}) {
  const scannerRef = useRef(null);
  const detectedRef = useRef(false);
  const hintTimerRef = useRef(null);
  const [permState, setPermState] = useState("pending"); // pending | granted | denied | unavailable
  const [showHint, setShowHint] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [capturing, setCapturing] = useState(false);
  // Torch (flashlight) — only offered when the camera reports support.
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  useEffect(() => {
    if (!active) return;

    let html5 = null;
    detectedRef.current = false;
    setShowHint(false);
    setErrorMsg("");
    setPermState("pending");

    const start = async () => {
      try {
        html5 = new Html5Qrcode(CONTAINER_ID, {
          formatsToSupport: SUPPORTED_FORMATS,
          verbose: false,
        });
        scannerRef.current = html5;

        // Compute a barcode-shaped aim box: wide rectangle capped to viewport.
        // html5-qrcode enforces a 50px minimum on both edges and throws
        // synchronously from an internal video-ready callback if violated —
        // that throw happens outside the promise chain `start()` returns, so
        // our try/catch below can never catch it. A tiny/degenerate video
        // feed (camera still warming up, unusual driver) can otherwise drive
        // minEdge under ~59px and trip this. Clamp both edges to >=50 so the
        // library's own validation can never fire.
        const qrbox = (viewfinderWidth, viewfinderHeight) => {
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight) || 0;
          const width = Math.max(50, Math.floor(minEdge * 0.85));
          // Barcodes are ~2:1 wide — keep aspect ratio close to that, but
          // never below the library's own minimum.
          const height = Math.max(50, Math.floor(width * 0.42));
          return { width, height };
        };

        await html5.start(
          { facingMode: "environment" },
          {
            fps: 12,
            qrbox,
            aspectRatio: 1.7,
            disableFlip: true,
          },
          (decoded) => {
            if (detectedRef.current) return;
            const clean = String(decoded || "").trim();
            const digits = clean.replace(/\D/g, "");
            if (!/^\d{8,14}$/.test(digits)) return;
            detectedRef.current = true;
            haptic();
            onDetected?.(digits);
          },
          () => { /* per-frame decode misses — ignore */ }
        );
        setPermState("granted");

        // Torch capability check (Android Chrome mostly; hidden elsewhere).
        try {
          const caps = html5.getRunningTrackCapabilities?.();
          setTorchSupported(!!caps?.torch);
        } catch { setTorchSupported(false); }

        hintTimerRef.current = setTimeout(() => {
          if (!detectedRef.current) setShowHint(true);
        }, HINT_AFTER_MS);
      } catch (e) {
        const msg = (e?.message || "").toLowerCase();
        if (msg.includes("permission") || msg.includes("notallowederror") || msg.includes("denied")) {
          setPermState("denied");
        } else if (msg.includes("notfounderror") || msg.includes("no camera") || msg.includes("no cameras")) {
          setPermState("unavailable");
        } else {
          setPermState("denied");
          setErrorMsg(e?.message || "Could not start the camera");
        }
      }
    };

    const t = setTimeout(start, 80);
    return () => {
      clearTimeout(t);
      setTorchOn(false);
      setTorchSupported(false);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch { /* noop */ }
        try { scannerRef.current.clear?.(); } catch { /* noop */ }
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Iter 52: shutter — capture current video frame as a JPEG data-URL.
  // html5-qrcode mounts the <video> inside CONTAINER_ID; grab it and paint
  // to an off-screen canvas.
  const captureFrame = async () => {
    if (capturing) return;
    setCapturing(true);
    try {
      const container = document.getElementById(CONTAINER_ID);
      const videoEl = container?.querySelector("video");
      if (!videoEl || !videoEl.videoWidth) {
        throw new Error("video not ready");
      }
      const canvas = document.createElement("canvas");
      // Downscale to max 1600px on the long edge — mirrors what the food
      // scanner uses and keeps Gemini tile count reasonable.
      const scale = Math.min(1, 1600 / Math.max(videoEl.videoWidth, videoEl.videoHeight));
      canvas.width = Math.round(videoEl.videoWidth * scale);
      canvas.height = Math.round(videoEl.videoHeight * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      haptic();
      onCapture?.(dataUrl);
    } catch (e) {
      setErrorMsg(e?.message || "Could not capture frame");
    } finally {
      setCapturing(false);
    }
  };

  const toggleTorch = async () => {
    const next = !torchOn;
    try {
      await scannerRef.current?.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      // fallback: apply straight on the media track
      try {
        const videoEl = document.getElementById(CONTAINER_ID)?.querySelector("video");
        const track = videoEl?.srcObject?.getVideoTracks?.()[0];
        await track?.applyConstraints({ advanced: [{ torch: next }] });
        setTorchOn(next);
      } catch { /* torch flaked — leave state as-is */ }
    }
  };

  return (
    <div className="bg-white rounded-[16px] border border-line2 overflow-hidden" data-testid="barcode-scanner">
      <div className="relative" style={{ background: "#0F0F10", aspectRatio: "4 / 3" }}>
        <div
          id={CONTAINER_ID}
          className="absolute inset-0"
          data-testid="barcode-video-mount"
          style={{ background: "#0F0F10" }}
        />

        {permState !== "denied" && permState !== "unavailable" && (
          <>
            <div
              className="absolute inset-0 grid place-items-center pointer-events-none"
              data-testid="barcode-aim-guide"
            >
              <div
                className="border-2"
                style={{
                  width: labelMode ? "82%" : "80%",
                  aspectRatio: labelMode ? "1.3 / 1" : "2.35 / 1",
                  borderColor: "#00D4FF",
                  borderRadius: 12,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
                }}
              />
            </div>
            <div
              className="absolute left-0 right-0 text-center text-white pointer-events-none"
              style={{ top: 14, fontSize: 13, fontWeight: 500, textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
              data-testid="barcode-helper-text"
            >
              {labelMode
                ? "Point at the nutrition label"
                : "Point at the barcode or nutrition label"}
            </div>

            {/* Torch — only when the camera supports it */}
            {torchSupported && (
              <button
                type="button"
                onClick={toggleTorch}
                aria-label={torchOn ? "Turn flashlight off" : "Turn flashlight on"}
                data-testid="torch-toggle"
                className="absolute grid place-items-center rounded-full"
                style={{
                  top: 10,
                  right: 10,
                  width: 40,
                  height: 40,
                  background: torchOn ? "#FFFFFF" : "rgba(0,0,0,0.45)",
                  border: "1px solid rgba(255,255,255,0.35)",
                }}
              >
                <Flashlight size={18} color={torchOn ? "#000" : "#FFF"} />
              </button>
            )}

            {/* Iter 52: shutter — capture the label frame at any time */}
            <div
              className="absolute left-0 right-0 grid place-items-center"
              style={{ bottom: 16 }}
            >
              <button
                type="button"
                onClick={captureFrame}
                disabled={capturing || permState !== "granted"}
                aria-label="Capture label"
                data-testid="label-capture-shutter"
                className="grid place-items-center rounded-full"
                style={{
                  width: 60,
                  height: 60,
                  background: "#FFFFFF",
                  border: "4px solid rgba(255,255,255,0.35)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                  opacity: capturing ? 0.5 : 1,
                }}
              >
                <Camera size={22} color="#000" />
              </button>
            </div>
          </>
        )}

        {(permState === "denied" || permState === "unavailable") && (
          <div
            className="absolute inset-0 grid place-items-center px-6"
            data-testid="barcode-perm-denied"
            style={{ background: "rgba(15,15,16,0.94)" }}
          >
            <div className="text-center max-w-sm">
              <div
                className="w-12 h-12 mx-auto rounded-full grid place-items-center"
                style={{ background: "rgba(245,197,24,0.18)" }}
              >
                <AlertTriangle size={22} color="#F5C518" />
              </div>
              <h3
                className="mt-3 font-barlow-cond"
                style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF" }}
              >
                {permState === "unavailable" ? "No camera found" : "Camera access needed"}
              </h3>
              <p className="mt-1 text-[12px]" style={{ color: "rgba(255,255,255,0.72)" }}>
                {permState === "unavailable"
                  ? "We couldn't find a camera on this device. Enter the code manually below."
                  : "Camera access is needed to read the pack. Enable it in your browser settings, or enter the code manually below."}
              </p>
              {errorMsg ? (
                <p className="mt-2 text-[10px]" style={{ color: "rgba(255,255,255,0.5)" }}>
                  {errorMsg}
                </p>
              ) : null}
            </div>
          </div>
        )}

        {showHint && permState === "granted" && (
          <div
            className="absolute left-3 right-3"
            data-testid="barcode-hint"
            style={{
              bottom: 90,
              background: "rgba(0,0,0,0.72)",
              color: "#fff",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 12,
              textAlign: "center",
              backdropFilter: "blur(8px)",
            }}
          >
            Trouble scanning? Tap the shutter to read the label directly.
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onManualClick}
        data-testid="barcode-manual-toggle"
        className="w-full h-11 flex items-center justify-center gap-2 text-[13px] font-semibold"
        style={{ color: "#4A4845", background: "#F5F3EE", borderTop: "1px solid #E2E0DC" }}
      >
        <Keyboard size={14} /> Enter code manually
      </button>
    </div>
  );
}
